import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaChevronLeft,
  FaPencilAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSpinner,
  FaInfoCircle,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type WritingTask = {
  taskId: number;
  taskDescription: string;
  level: string;
  deadline: string;
  completed: boolean;
  score: number | null;
};

const StudentMyWritingTasksPage: React.FC = () => {
  const { student } = useAuth();
  const navigate = useNavigate();

  const [writingTasks, setWritingTasks] = useState<WritingTask[]>([]);
  const [batch, setBatch] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    fetchData();
  }, [student]);

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const batchResponse = await fetch(
        `${API_BASE_URL}/api/student/my-batch`,
        {
          credentials: "include",
        }
      );

      if (batchResponse.ok) {
        const batchData = await batchResponse.json();
        setBatch(batchData);

        const batchId = batchData.id || batchData.batchId;
        const studentId = student?.studentId;

        if (batchId && studentId) {
          const tasksResponse = await fetch(
            `${API_BASE_URL}/api/student/writing-tasks/my-batch?batchId=${batchId}&studentId=${studentId}`,
            { credentials: "include" }
          );

          if (tasksResponse.ok) {
            const tasksData = await tasksResponse.json();
            setWritingTasks(tasksData);
          } else {
            setError("Failed to load writing tasks");
          }
        } else {
          setError("Missing batch or student information");
        }
      } else {
        setError("Failed to load batch information");
      }
    } catch {
      setError("Failed to load writing tasks");
    } finally {
      setLoading(false);
    }
  };

  const getTaskStatus = (task: WritingTask) => {
    if (task.completed) return "completed";
    if (task.deadline && new Date(task.deadline) < new Date()) return "missed";
    return "pending";
  };

  const truncateText = (text: string, maxLength: number = 80) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaPencilAlt />}
        title="My Writing Tasks"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Writing Tasks" },
        ]}
      />

      {/* Info Card */}
      <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
          <FaInfoCircle className="text-blue-600" />
          How It Works
        </h3>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">1.</span>
            <span>View your assigned writing tasks below</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">2.</span>
            <span>Click "Start Task" to begin writing in German</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">3.</span>
            <span>Submit your writing before the deadline</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">4.</span>
            <span>
              Get instant feedback with corrections, an optimized version, and a
              score out of 10
            </span>
          </li>
        </ul>
      </div>

      {/* Writing Tasks Section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white p-6">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <FaPencilAlt />
            Your Writing Tasks
          </h3>
          <p className="text-cyan-100 text-sm mt-1">
            Complete tasks and track your progress
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <FaSpinner className="animate-spin text-[#009fe3] text-4xl mx-auto mb-4" />
              <p className="text-gray-600">Loading your writing tasks...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          ) : !batch ? (
            <div className="text-center py-12 bg-amber-50 border border-amber-200 rounded-xl">
              <FaPencilAlt className="mx-auto h-12 w-12 text-amber-400 mb-4" />
              <h4 className="text-lg font-medium text-gray-700 mb-2">
                No Batch Assigned
              </h4>
              <p className="text-gray-500 text-sm">
                You need to be assigned to a batch to access writing tasks.
              </p>
            </div>
          ) : writingTasks.length === 0 ? (
            <div className="text-center py-12">
              <FaPencilAlt className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h4 className="text-lg font-medium text-gray-700 mb-2">
                No Writing Tasks Yet
              </h4>
              <p className="text-gray-500 text-sm">
                No writing tasks have been assigned to your batch yet.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                      Level
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                      Task Description
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                      Deadline
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                      Status
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                      Score
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {writingTasks.map((task, index) => {
                    const status = getTaskStatus(task);
                    return (
                      <tr
                        key={task.taskId}
                        className={`border-b border-gray-100 ${
                          index % 2 === 1 ? "bg-gray-50" : "bg-white"
                        } hover:bg-blue-50 transition-colors`}
                      >
                        <td className="py-4 px-4">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                            {task.level}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-gray-700 text-sm">
                            {truncateText(task.taskDescription, 80)}
                          </p>
                        </td>
                        <td className="py-4 px-4 text-gray-600 text-sm">
                          {task.deadline ? (
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-1 font-medium">
                                <FaClock className="text-xs" />
                                {new Date(task.deadline).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  }
                                )}
                              </div>
                              <div className="text-xs text-gray-500 ml-4">
                                {new Date(task.deadline).toLocaleTimeString(
                                  "en-US",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )}
                              </div>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {status === "completed" && (
                            <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full text-xs font-semibold border border-emerald-200">
                              <FaCheckCircle className="text-emerald-500" />
                              Completed
                            </span>
                          )}
                          {status === "pending" && (
                            <span className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 px-3 py-1 rounded-full text-xs font-semibold border border-amber-200">
                              <FaClock className="text-amber-500" />
                              Pending
                            </span>
                          )}
                          {status === "missed" && (
                            <span className="inline-flex items-center gap-1.5 text-rose-700 bg-rose-50 px-3 py-1 rounded-full text-xs font-semibold border border-rose-200">
                              <FaTimesCircle className="text-rose-500" />
                              Missed
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {task.score !== null ? (
                            <span className="font-semibold text-gray-900">
                              {task.score}/10
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <button
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm ${
                              status === "pending"
                                ? "bg-[#009fe3] text-white hover:bg-cyan-600 hover:shadow-md"
                                : status === "completed"
                                ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"
                            }`}
                            onClick={() =>
                              status !== "missed" &&
                              navigate(`/student/writing-task/${task.taskId}`)
                            }
                            disabled={status === "missed"}
                            title={
                              status === "completed"
                                ? "View submission"
                                : status === "missed"
                                ? "Deadline missed"
                                : "Start task"
                            }
                          >
                            <FaPencilAlt />
                            {status === "completed" ? "View" : "Start Task"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentMyWritingTasksPage;
